import { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RiDeleteBinFill, RiPencilLine } from "react-icons/ri";
import { selectAuthMe } from "../../Redux/Slices/authSlice";
import MiniEditor from "./MiniEditor";
import SegmentedTabs from "../../Utility/SegmentedTabs.jsx/SegmentedTabs.jsx";
import Button from "../../Utility/Button/Button";
import FloatingTooltip from "../../Utility/FloatingTooltip/FloatingTooltip";
import DeleteConfirmModal from "../../Utility/DeleteConfirmModal/DeleteConfirmModal";
import { Loader } from "../../Utility/Loader";
import {
  addWorkItemComment,
  deleteWorkItemComment,
  deleteWorkItemCommentAttachment,
  fetchWorkItemComments,
  fetchWorkItemHistory,
  getWorkItemCommentAttachmentDownloadUrl,
  updateWorkItemComment,
} from "../../Redux/Thunks/projectManagementThunk";
import {
  parseWorkItemCommentsPayload,
  parseWorkItemHistoryPayload,
  unwrapPostMutationPayload,
  sortWorkItemCommentsNewestFirst,
  resolveWorkItemCommentTimestamp,
  mapWorkItemAttachmentUrlResponse,
} from "../Projects/projectMappers";
import CommentAttachmentChip from "./CommentAttachmentChip";
import DescriptionImageSizeModal from "../../Utility/DescriptionEditor/DescriptionImageSizeModal";
import { splitCommentAttachments } from "./commentAttachmentUtils";
import WorkItemTimeLogsSection from "./WorkItemTimeLogsSection";
import toast from "react-hot-toast";
import { formatDateTime } from "../../utils/dateUtils";
import { isSilentRequestAbortError } from "../../utils/apiErrorMessage";
import {
  extractMentionedUserIdsFromHtml,
  normalizeCommentHtmlForDisplay,
  stripTrailingCommentWhitespace,
} from "./mentionUtils";

/**
 * A comment whose whole body is a pasted/dropped image has no text and no uploaded file — the
 * image rides along inside the HTML — so the emptiness guards have to look for it there or the
 * submit is silently rejected and the Comment button looks dead.
 */
function htmlHasInlineImage(html) {
  return /<img\b/i.test(String(html ?? ""));
}

const ACTIVITY_TABS = [
  { id: "comments", label: "Comments" },
  { id: "history", label: "History" },
  { id: "worklog", label: "Worklog" },
];

function formatCommentTimestamp(isoDate) {
  if (!isoDate) return "";
  const s = formatDateTime(isoDate);
  return s === "N/A" ? String(isoDate) : s;
}

function commentAuthorUserId(u) {
  if (!u || typeof u !== "object") return null;
  const id = u.id ?? u.userId;
  return id != null && String(id).trim() !== "" ? String(id) : null;
}

function isCommentByCurrentUser(u, currentUser) {
  if (!currentUser || typeof currentUser !== "object") return false;
  const theirId = commentAuthorUserId(u);
  const myId = currentUser.id != null ? String(currentUser.id) : null;
  if (myId && theirId && myId === theirId) return true;
  const myEmail = currentUser.email != null ? String(currentUser.email).trim().toLowerCase() : "";
  const theirEmail = u?.email != null ? String(u.email).trim().toLowerCase() : "";
  return Boolean(myEmail && theirEmail && myEmail === theirEmail);
}

/** Map API comment to display shape { id, author, date, content }. */
function mapApiCommentsToDisplay(apiComments, currentUser) {
  if (!Array.isArray(apiComments)) return [];
  return apiComments.map((c) => {
    const u =
      c?.user ??
      (c?.userId != null || c?.user_id != null
        ? { id: c.userId ?? c.user_id, email: c.userEmail ?? c.user_email }
        : null);
    const author = isCommentByCurrentUser(u, currentUser)
      ? "You"
      : [u?.firstName, u?.lastName].filter(Boolean).join(" ").trim() ||
        u?.name ||
        u?.email ||
        "Unknown";
    const timestamp = resolveWorkItemCommentTimestamp(c);
    return {
      id: c?.id ?? String(c?.comment ?? "") + String(timestamp ?? c?.created_at ?? ""),
      author,
      date: formatCommentTimestamp(timestamp),
      timestamp,
      content: normalizeCommentHtmlForDisplay(c?.comment ?? c?.content ?? ""),
      rawComment: c?.comment ?? c?.content ?? "",
      attachments: Array.isArray(c?.attachments) ? c.attachments : [],
    };
  });
}

const PAGE_SIZE = 5;
/** History tab: show this many rows per page (full list still loaded in one GET). */
const HISTORY_PAGE_SIZE = 7;

export default function ActivityTabs({
  history,
  projectId,
  workItemId,
  workItemKey = "",
  workItemTitle = "",
  initialComments = [],
  refreshKey = 0,
  mentionUsers = [],
  onRefresh,
}) {
  const dispatch = useDispatch();
  const me = useSelector(selectAuthMe);
  const [activeTab, setActiveTab] = useState("comments");
  const [rawComments, setRawComments] = useState(() =>
    Array.isArray(initialComments) ? initialComments : []
  );
  const displayComments = useMemo(
    () => mapApiCommentsToDisplay(sortWorkItemCommentsNewestFirst(rawComments), me),
    [rawComments, me]
  );
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentsPage, setCommentsPage] = useState(1);
  const [commentsTotalPages, setCommentsTotalPages] = useState(1);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [updatingCommentId, setUpdatingCommentId] = useState(null);
  const [commentToDelete, setCommentToDelete] = useState(null);
  const [deletingCommentId, setDeletingCommentId] = useState(null);
  const [downloadingCommentAttachmentId, setDownloadingCommentAttachmentId] = useState(null);
  const [removingCommentAttachmentId, setRemovingCommentAttachmentId] = useState(null);
  const [pastedImageUrls, setPastedImageUrls] = useState({});
  const fetchedPastedImageAttachmentIdsRef = useRef(new Set());
  const [historyItems, setHistoryItems] = useState(Array.isArray(history) ? history : []);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotalPages, setHistoryTotalPages] = useState(1);
  // Call comments API only for the current work item: dynamic projectId and workItemId from URL (no batch/list calls).
  useEffect(() => {
    if (!projectId || !workItemId) return;
    setCommentsLoading(true);
    dispatch(
      fetchWorkItemComments({
        projectId,
        workItemId,
        page: 1,
        limit: PAGE_SIZE,
      })
    )
      .unwrap()
      .then((raw) => {
        const { list, meta } = parseWorkItemCommentsPayload(raw);
        setRawComments(list ?? []);
        setCommentsPage(1);
        setCommentsTotalPages(meta?.totalPages ?? 1);
      })
      .catch(() => {})
      .finally(() => setCommentsLoading(false));
  }, [dispatch, projectId, workItemId, refreshKey]);

  // Pasted images (identified by the backend's "pasted-image-N.ext" naming) get an inline
  // preview in the list, so resolve their view URL eagerly — tracked ids are never re-fetched.
  useEffect(() => {
    if (!projectId || !workItemId) return;
    const toFetch = [];
    displayComments.forEach((comment) => {
      const { pastedImages } = splitCommentAttachments(comment.attachments);
      pastedImages.forEach((attachment) => {
        const attachmentId = attachment?.id != null ? String(attachment.id) : "";
        if (!attachmentId || fetchedPastedImageAttachmentIdsRef.current.has(attachmentId)) return;
        fetchedPastedImageAttachmentIdsRef.current.add(attachmentId);
        toFetch.push({ commentId: comment.id, attachmentId });
      });
    });
    toFetch.forEach(({ commentId, attachmentId }) => {
      dispatch(
        getWorkItemCommentAttachmentDownloadUrl({ projectId, workItemId, commentId, attachmentId })
      )
        .unwrap()
        .then((raw) => {
          const url = mapWorkItemAttachmentUrlResponse(raw);
          if (url) setPastedImageUrls((prev) => ({ ...prev, [attachmentId]: url }));
        })
        .catch(() => {});
    });
  }, [displayComments, dispatch, projectId, workItemId]);

  const handleCommentsPageChange = (nextPage) => {
    if (!projectId || !workItemId || nextPage === commentsPage || nextPage < 1) return;
    setCommentsLoading(true);
    dispatch(
      fetchWorkItemComments({
        projectId,
        workItemId,
        page: nextPage,
        limit: PAGE_SIZE,
      })
    )
      .unwrap()
      .then((raw) => {
        const { list, meta } = parseWorkItemCommentsPayload(raw);
        setRawComments(list ?? []);
        setCommentsPage(nextPage);
        setCommentsTotalPages(meta?.totalPages ?? commentsTotalPages ?? 1);
      })
      .finally(() => setCommentsLoading(false));
  };

  const handleCommentSubmit = async (commentData) => {
    const html = (commentData?.html ?? "").trim();
    const text = (commentData?.text ?? html ?? "").trim();
    const files = Array.isArray(commentData?.files) ? commentData.files.filter(Boolean) : [];
    const hasFiles = files.length > 0;
    const hasBody = Boolean(text) || htmlHasInlineImage(html);
    // Returns whether the comment persisted — MiniEditor keeps the composer's text and pending
    // attachments on `false` so a failed upload is recoverable instead of silently discarded.
    if (!hasBody && !hasFiles) return false;
    if (!projectId || !workItemId) {
      toast.error("Cannot add comment: project or work item not found.");
      return false;
    }
    const commentPayload = stripTrailingCommentWhitespace(html || text);
    if (!String(commentPayload).trim() && !hasFiles) return false;
    const mentionedUserIds = extractMentionedUserIdsFromHtml(commentPayload);
    setCommentSubmitting(true);
    try {
      const result = await dispatch(
        addWorkItemComment({
          projectId,
          workItemId,
          comment: commentPayload,
          mentionedUserIds,
          files,
        })
      ).unwrap();
      const data = unwrapPostMutationPayload(result);
      const newRow =
        data && typeof data === "object"
          ? { ...data, user: data.user ?? me ?? undefined }
          : {
              id: Date.now(),
              comment: commentPayload,
              timestamp: new Date().toISOString(),
              created_at: new Date().toISOString(),
              user: me ?? undefined,
            };
      setRawComments((prev) => sortWorkItemCommentsNewestFirst([...prev, newRow]));
      toast.success("Comment added.");
      return true;
    } catch (err) {
      toast.error(err?.message ?? "Failed to add comment.");
      return false;
    } finally {
      setCommentSubmitting(false);
    }
  };

  const handleCommentUpdate = async (commentId, commentData) => {
    const html = (commentData?.html ?? "").trim();
    const text = (commentData?.text ?? html ?? "").trim();
    const files = Array.isArray(commentData?.files) ? commentData.files.filter(Boolean) : [];
    // Existing (already-persisted) attachments count too — clearing all text on a comment that
    // still has an attachment left (new or existing) must stay saveable.
    const existingComment = displayComments.find((c) => String(c.id) === String(commentId));
    const hasAnyAttachments = files.length > 0 || (existingComment?.attachments?.length ?? 0) > 0;
    const hasBody = Boolean(text) || htmlHasInlineImage(html);
    if ((!hasBody && !hasAnyAttachments) || !projectId || !workItemId || !commentId) return false;
    const commentPayload = stripTrailingCommentWhitespace(html || text);
    if (!String(commentPayload).trim() && !hasAnyAttachments) return false;
    const mentionedUserIds = extractMentionedUserIdsFromHtml(commentPayload);
    setUpdatingCommentId(commentId);
    try {
      const result = await dispatch(
        updateWorkItemComment({
          projectId,
          workItemId,
          commentId,
          comment: commentPayload,
          mentionedUserIds,
          files,
        })
      ).unwrap();
      const data = unwrapPostMutationPayload(result);
      setRawComments((prev) =>
        prev.map((c) => {
          if (String(c?.id) !== String(commentId)) return c;
          const nextComment = data?.comment ?? commentPayload;
          return {
            ...c,
            ...(data && typeof data === "object" ? data : {}),
            comment: nextComment,
          };
        })
      );
      setEditingCommentId(null);
      toast.success("Comment updated.");
      return true;
    } catch (err) {
      toast.error(err?.message ?? "Failed to update comment.");
      return false;
    } finally {
      setUpdatingCommentId(null);
    }
  };

  const handleConfirmDeleteComment = async () => {
    if (!commentToDelete?.id || !projectId || !workItemId) return;
    setDeletingCommentId(commentToDelete.id);
    try {
      await dispatch(
        deleteWorkItemComment({
          projectId,
          workItemId,
          commentId: commentToDelete.id,
        })
      ).unwrap();
      setRawComments((prev) => prev.filter((c) => String(c?.id) !== String(commentToDelete.id)));
      if (String(editingCommentId) === String(commentToDelete.id)) {
        setEditingCommentId(null);
      }
      setCommentToDelete(null);
      toast.success("Comment deleted.");
    } catch (err) {
      toast.error(err?.message ?? "Failed to delete comment.");
    } finally {
      setDeletingCommentId(null);
    }
  };

  const handleDownloadCommentAttachment = async (commentId, attachment) => {
    const attachmentId = attachment?.id;
    if (!projectId || !workItemId || !commentId || !attachmentId) return;
    setDownloadingCommentAttachmentId(String(attachmentId));
    try {
      const raw = await dispatch(
        getWorkItemCommentAttachmentDownloadUrl({ projectId, workItemId, commentId, attachmentId })
      ).unwrap();
      const url = mapWorkItemAttachmentUrlResponse(raw);
      if (url) window.open(url, "_blank", "noopener,noreferrer");
      else toast.error("Could not get download URL");
    } catch (err) {
      toast.error(err?.message ?? "Failed to get download link");
    } finally {
      setDownloadingCommentAttachmentId(null);
    }
  };

  const handleRemoveCommentAttachment = async (commentId, attachment) => {
    const attachmentId = attachment?.id;
    if (!projectId || !workItemId || !commentId || !attachmentId) return;
    setRemovingCommentAttachmentId(String(attachmentId));
    try {
      await dispatch(
        deleteWorkItemCommentAttachment({ projectId, workItemId, commentId, attachmentId })
      ).unwrap();
      setRawComments((prev) =>
        prev.map((c) => {
          if (String(c?.id) !== String(commentId)) return c;
          const nextAttachments = Array.isArray(c?.attachments)
            ? c.attachments.filter((a) => String(a?.id) !== String(attachmentId))
            : [];
          return { ...c, attachments: nextAttachments };
        })
      );
    } catch (err) {
      toast.error(err?.message ?? "Failed to remove attachment");
    } finally {
      setRemovingCommentAttachmentId(null);
    }
  };

  // Load history for current work item from API
  useEffect(() => {
    if (!projectId || !workItemId) return;
    setHistoryLoading(true);
    dispatch(fetchWorkItemHistory({ projectId, workItemId }))
      .unwrap()
      .then((raw) => {
        const list = parseWorkItemHistoryPayload(raw);
        const historyList = Array.isArray(list) ? list : [];
        setHistoryItems(historyList);
        setHistoryPage(1);
        setHistoryTotalPages(Math.max(1, Math.ceil(historyList.length / HISTORY_PAGE_SIZE)));
      })
      .catch((err) => {
        if (isSilentRequestAbortError(err)) return;
        setHistoryItems([]);
        setHistoryPage(1);
        setHistoryTotalPages(1);
      })
      .finally(() => setHistoryLoading(false));
  }, [dispatch, projectId, workItemId, refreshKey]);

  const paginatedHistoryItems = historyItems.slice(
    (historyPage - 1) * HISTORY_PAGE_SIZE,
    historyPage * HISTORY_PAGE_SIZE
  );

  const handleHistoryPageChange = (nextPage) => {
    if (nextPage < 1 || nextPage > historyTotalPages || nextPage === historyPage) return;
    setHistoryPage(nextPage);
  };
  return (
    <section>
      <SegmentedTabs tabs={ACTIVITY_TABS} activeTab={activeTab} onChange={setActiveTab} />
      {activeTab === "comments" && (
        <CommentBox
          comments={displayComments}
          onCommentSubmit={handleCommentSubmit}
          submitting={commentSubmitting}
          loading={commentsLoading}
          page={commentsPage}
          totalPages={commentsTotalPages}
          onPageChange={handleCommentsPageChange}
          mentionUsers={mentionUsers}
          editingCommentId={editingCommentId}
          updatingCommentId={updatingCommentId}
          onStartEdit={setEditingCommentId}
          onCancelEdit={() => setEditingCommentId(null)}
          onUpdateComment={handleCommentUpdate}
          onDeleteComment={(comment) => setCommentToDelete(comment)}
          onDownloadAttachment={handleDownloadCommentAttachment}
          onRemoveExistingAttachment={handleRemoveCommentAttachment}
          downloadingAttachmentId={downloadingCommentAttachmentId}
          removingAttachmentId={removingCommentAttachmentId}
          pastedImageUrls={pastedImageUrls}
        />
      )}
      {activeTab === "history" && (
        <HistoryList
          history={paginatedHistoryItems}
          loading={historyLoading}
          page={historyPage}
          totalPages={historyTotalPages}
          onPageChange={handleHistoryPageChange}
        />
      )}
      {activeTab === "worklog" && projectId && workItemId ? (
        <WorkItemTimeLogsSection
          projectId={projectId}
          workItemId={workItemId}
          workItemKey={workItemKey}
          workItemTitle={workItemTitle}
          refreshKey={refreshKey}
          onRefresh={onRefresh}
          embedded
        />
      ) : null}
      <DeleteConfirmModal
        isOpen={!!commentToDelete}
        onClose={() => !deletingCommentId && setCommentToDelete(null)}
        onConfirm={handleConfirmDeleteComment}
        title="Delete comment"
        message={
          <>
            Are you sure you want to delete this comment
            {commentToDelete?.author ? (
              <>
                {" "}
                by <strong>{commentToDelete.author}</strong>
              </>
            ) : null}
            ? This action cannot be undone.
          </>
        }
        loading={!!deletingCommentId}
        confirmLabel="Delete"
      />
    </section>
  );
}

function CommentBox({
  comments = [],
  onCommentSubmit,
  submitting = false,
  loading = false,
  page = 1,
  totalPages = 1,
  onPageChange,
  mentionUsers = [],
  editingCommentId = null,
  updatingCommentId = null,
  onStartEdit,
  onCancelEdit,
  onUpdateComment,
  onDeleteComment,
  onDownloadAttachment,
  onRemoveExistingAttachment,
  downloadingAttachmentId = null,
  removingAttachmentId = null,
  pastedImageUrls = {},
}) {
  const [previewImageSrc, setPreviewImageSrc] = useState(null);

  const handleCommentBodyClick = (e) => {
    if (e.target?.tagName === "IMG") {
      setPreviewImageSrc(e.target.getAttribute("src"));
    }
  };

  return (
    <>
      <MiniEditor
        placeholder="Add a comment…"
        onSubmit={onCommentSubmit}
        disabled={submitting}
        mentionUsers={mentionUsers}
        attachmentsEnabled
        enableImageResize
      />
      {loading ? (
        <div className="flex items-center justify-center py-6">
          <Loader variant="inline" size="sm" label="Loading comments" />
        </div>
      ) : (
        <>
          {comments.map((comment) => {
            const isEditing = String(editingCommentId) === String(comment.id);
            const isUpdating = String(updatingCommentId) === String(comment.id);
            return (
              <div key={comment.id} className="comment">
                <div className="comment-row-header">
                  <span className="comment-row-meta">
                    <b>{comment.author}</b>
                    {comment.date ? (
                      <time
                        className="comment-row-timestamp"
                        dateTime={comment.timestamp ?? undefined}
                      >
                        {" "}
                        – {comment.date}
                      </time>
                    ) : null}
                  </span>
                  <div className="comment-row-actions">
                    <FloatingTooltip label="Edit comment">
                      <Button
                        type="button"
                        size="sm"
                        className="btn-warning-new !w-7 !h-7 !p-0 flex items-center justify-center border-0"
                        aria-label="Edit comment"
                        disabled={isUpdating}
                        onClick={() => onStartEdit?.(comment.id)}
                      >
                        <RiPencilLine className="text-white text-[12px]" />
                      </Button>
                    </FloatingTooltip>
                    <FloatingTooltip label="Delete comment">
                      <Button
                        type="button"
                        size="sm"
                        className="btn-danger-new !w-7 !h-7 !p-0 flex items-center justify-center border-0"
                        aria-label="Delete comment"
                        disabled={isUpdating}
                        onClick={() => onDeleteComment?.(comment)}
                      >
                        <RiDeleteBinFill className="text-white text-[12px]" />
                      </Button>
                    </FloatingTooltip>
                  </div>
                </div>
                {isEditing ? (
                  <div className="comment-row-editor">
                    <MiniEditor
                      key={`edit-comment-${comment.id}`}
                      value={comment.rawComment}
                      onSubmit={(data) => onUpdateComment?.(comment.id, data)}
                      onCancel={onCancelEdit}
                      disabled={isUpdating}
                      isEditMode
                      mentionUsers={mentionUsers}
                      attachmentsEnabled
                      enableImageResize
                      existingAttachments={comment.attachments}
                      onRemoveExistingAttachment={(attachment) =>
                        onRemoveExistingAttachment?.(comment.id, attachment)
                      }
                      removingAttachmentId={removingAttachmentId}
                      pastedImageUrls={pastedImageUrls}
                    />
                  </div>
                ) : (
                  <>
                    <div
                      className="comment-body mini-editor-content"
                      dangerouslySetInnerHTML={{ __html: comment.content }}
                      onClick={handleCommentBodyClick}
                    />
                    {(() => {
                      const { pastedImages, other } = splitCommentAttachments(comment.attachments);
                      return (
                        <>
                          {pastedImages.length > 0 ? (
                            <div className="comment-pasted-images">
                              {pastedImages.map((attachment) => {
                                const attachmentId =
                                  attachment?.id != null ? String(attachment.id) : "";
                                const url = attachmentId ? pastedImageUrls[attachmentId] : null;
                                if (!url) return null;
                                return (
                                  <img
                                    key={attachmentId}
                                    src={url}
                                    alt={attachment?.name ?? "Pasted image"}
                                    className="comment-pasted-image-preview"
                                    onClick={() => setPreviewImageSrc(url)}
                                  />
                                );
                              })}
                            </div>
                          ) : null}
                          {other.length > 0 ? (
                            <div className="mini-editor-attachments comment-posted-attachments">
                              {other.map((attachment) => {
                                const attachmentId =
                                  attachment?.id != null ? String(attachment.id) : "";
                                const name =
                                  attachment?.name ??
                                  attachment?.originalName ??
                                  attachment?.fileName ??
                                  "Attachment";
                                const mimeType =
                                  attachment?.mimeType ?? attachment?.contentType ?? "";
                                return (
                                  <CommentAttachmentChip
                                    key={attachmentId || name}
                                    name={name}
                                    isImage={String(mimeType).startsWith("image/")}
                                    previewUrl={attachment?.thumbnailUrl ?? null}
                                    onClick={() => onDownloadAttachment?.(comment.id, attachment)}
                                    busy={
                                      attachmentId !== "" &&
                                      downloadingAttachmentId === attachmentId
                                    }
                                  />
                                );
                              })}
                            </div>
                          ) : null}
                        </>
                      );
                    })()}
                  </>
                )}
              </div>
            );
          })}
          {totalPages > 1 && onPageChange && (
            <div className="flex items-center gap-3 mt-3 text-sm">
              <button
                type="button"
                onClick={() => onPageChange(page - 1)}
                disabled={page <= 1}
                className="px-2 py-1 border border-border-primary rounded disabled:opacity-50"
              >
                Prev
              </button>
              <span className="text-text-secondary">
                Page {page} of {totalPages}
              </span>
              <button
                type="button"
                onClick={() => onPageChange(page + 1)}
                disabled={page >= totalPages}
                className="px-2 py-1 border border-border-primary rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
      <DescriptionImageSizeModal
        isOpen={!!previewImageSrc}
        src={previewImageSrc}
        onClose={() => setPreviewImageSrc(null)}
        readOnly
      />
    </>
  );
}

function HistoryList({ history = [], loading = false, page = 1, totalPages = 1, onPageChange }) {
  if (loading) {
    return <p className="text-text-secondary text-sm mt-2">Loading history...</p>;
  }
  if (!Array.isArray(history) || history.length === 0) {
    return <p className="text-text-secondary text-sm mt-2">No history available.</p>;
  }

  return (
    <>
      {history.map((h, i) => {
        const message =
          h?.summary ??
          h?.message ??
          h?.description ??
          (h?.user && h?.field
            ? `${h.user} changed ${h.field} from "${h.from}" to "${h.to}"`
            : null);
        return (
          <div key={h?.id ?? i} className="history-row">
            {message ?? JSON.stringify(h)}
          </div>
        );
      })}
      {totalPages > 1 && onPageChange && (
        <div className="flex items-center gap-3 mt-3 text-sm">
          <button
            type="button"
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
            className="px-2 py-1 border border-border-primary rounded disabled:opacity-50"
          >
            Prev
          </button>
          <span className="text-text-secondary">
            Page {page} of {totalPages}
          </span>
          <button
            type="button"
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages}
            className="px-2 py-1 border border-border-primary rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </>
  );
}
